import { createContext, useContext, useState, useEffect } from 'react'
import { getSupabase } from '../lib/supabase'
import { isSupabaseConfigured } from '../config'

const AuthContext = createContext(null)

const DEMO_STORAGE_KEY = 'ssv_demo_session'

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    initAuth()
  }, [])

  async function initAuth() {
    const client = getSupabase()

    if (!client) {
      // Demo mode - check localStorage
      initDemoMode()
      setLoading(false)
      return
    }

    // Get current session
    const { data: { session } } = await client.auth.getSession()

    if (session?.user) {
      setUser(session.user)
      await loadProfile(session.user.id)
    }

    // Listen for auth changes
    const { data: { subscription } } = client.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setUser(session.user)
        await loadProfile(session.user.id)
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        setProfile(null)
      }
    })

    setLoading(false)

    return () => subscription?.unsubscribe()
  }

  function initDemoMode() {
    try {
      const demoSession = localStorage.getItem(DEMO_STORAGE_KEY)
      if (demoSession) {
        const session = JSON.parse(demoSession)
        setUser(session.user)
        setProfile(session.profile)
      }
    } catch (e) {
      localStorage.removeItem(DEMO_STORAGE_KEY)
    }
  }

  async function loadProfile(userId) {
    const client = getSupabase()
    if (!client) return null

    const { data, error } = await client
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (!error && data) {
      setProfile(data)
    }

    return data
  }

  async function signIn(email, password) {
    const client = getSupabase()

    if (!client) {
      // Demo mode login
      return demoSignIn(email, password)
    }

    const { data, error } = await client.auth.signInWithPassword({
      email,
      password,
    })

    if (error) {
      return { user: null, error: error.message }
    }

    setUser(data.user)
    await loadProfile(data.user.id)

    return { user: data.user, error: null }
  }

  function demoSignIn(email, password) {
    // Require specific demo credentials
    if (email !== 'demo@secondshiftventures.com' || password !== 'demo123') {
      return {
        user: null,
        error: 'Invalid credentials. Demo mode requires demo@secondshiftventures.com / demo123'
      }
    }

    const demoUser = {
      id: 'demo-user-1',
      email: email,
      created_at: new Date().toISOString(),
    }

    const demoProfile = {
      id: 'demo-user-1',
      email: email,
      name: 'Demo User',
      role: 'admin', // Demo users get admin access to see all features
    }

    setUser(demoUser)
    setProfile(demoProfile)

    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify({
      user: demoUser,
      profile: demoProfile,
    }))

    return { user: demoUser, error: null }
  }

  async function signUp(email, password, name) {
    const client = getSupabase()

    if (!client) {
      return { user: null, error: 'Supabase not configured. Please set up your project.' }
    }

    const { data, error } = await client.auth.signUp({
      email,
      password,
      options: {
        data: { name },
      },
    })

    if (error) {
      return { user: null, error: error.message }
    }

    return { user: data.user, error: null }
  }

  async function signOut() {
    const client = getSupabase()

    if (!client) {
      // Demo mode logout
      localStorage.removeItem(DEMO_STORAGE_KEY)
      setUser(null)
      setProfile(null)
      return
    }

    await client.auth.signOut()
    setUser(null)
    setProfile(null)
  }

  async function resetPassword(email) {
    const client = getSupabase()

    if (!client) {
      return { error: 'Supabase not configured' }
    }

    const { error } = await client.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })

    return { error: error?.message }
  }

  const isAuthenticated = !!user
  const isAdmin = profile?.role === 'admin'
  const displayName = profile?.name || user?.email?.split('@')[0] || 'User'
  const initials = displayName.substring(0, 2).toUpperCase()
  const isDemoMode = !isSupabaseConfigured()

  const value = {
    user,
    profile,
    loading,
    isAuthenticated,
    isAdmin,
    displayName,
    initials,
    isDemoMode,
    signIn,
    signUp,
    signOut,
    resetPassword,
    loadProfile,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
